import { isProblemDetails, type components } from '@hpd/contract';
import { queryOptions } from '@tanstack/react-query';

import { api } from '../api.ts';
import { ApiProblem } from './me.ts';

// CB-REQ VEN-11 at v1.990 (venue slice 4C): a venue's monthly statement. Figures travel as integer
// minor units in strings, never through a float.

export type Statement = components['schemas']['StatementView'];
export type StatementKind = Statement['kind'];

function problem(error: unknown, fallback: string): ApiProblem {
  return isProblemDetails(error)
    ? new ApiProblem(error.type, error.detail ?? error.title)
    : new ApiProblem('unknown', fallback);
}

export const statementsQuery = (month: string) =>
  queryOptions({
    queryKey: ['statements', month],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/statements', { params: { query: { month } } });
      if (error) throw problem(error, 'Could not load the statements.');
      return data;
    },
  });

export const statementQuery = (venueId: string, month: string, kind: StatementKind) =>
  queryOptions({
    queryKey: ['statements', month, venueId, kind],
    queryFn: async () => {
      const { data, error } = await api.GET('/v1/statements/{venueId}/{month}', {
        params: { path: { venueId, month }, query: { kind } },
      });
      if (error) throw problem(error, 'Could not load that statement.');
      return data;
    },
  });

export interface VenueBillInput {
  venueInvoiceNumber: string;
  invoiceDate: string;
  amountMinor: string;
  gstMinor: string;
}

export async function recordVenueBill(venueId: string, month: string, input: VenueBillInput) {
  const { data, error } = await api.POST('/v1/statements/{venueId}/{month}/bill', {
    params: { path: { venueId, month } },
    body: input,
  });
  if (error) throw problem(error, 'Could not record the venue’s invoice.');
  return data;
}

export async function renderStatement(venueId: string, month: string, kind: StatementKind) {
  const { data, error } = await api.POST('/v1/statements/{venueId}/{month}/documents', {
    params: { path: { venueId, month }, query: { kind } },
  });
  if (error) throw problem(error, 'Could not make the PDF.');
  return data;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** "2026-10" → "October 2026". */
export function monthLabel(month: string): string {
  const [year, m] = month.split('-').map(Number) as [number, number];
  return `${MONTHS[m - 1]} ${year}`;
}

/** The month before or after "2026-10". */
export function shiftMonth(month: string, by: number): string {
  const [year, m] = month.split('-').map(Number) as [number, number];
  const index = year * 12 + (m - 1) + by;
  return `${Math.floor(index / 12)}-${String((index % 12) + 1).padStart(2, '0')}`;
}
